import { apiRequest } from '@/shared/api';
import { isTelegram } from './platform';

/**
 * Яндекс Метрика в веб-версии (WEB.md, этап 7) — тот же счётчик, что у
 * лендинга (`website/src/lib/metrika.ts`). Реклама ведёт прямо в приложение,
 * и без счётчика здесь Директ не видел бы, что было после клика.
 *
 * Только в браузере: в мини-аппе Telegram свой мир, и его воронку считают
 * офлайн-конверсии с ClientID из `/start`. Счётчик принимает данные только с
 * адресов из своих настроек — localhost отбрасывает сам.
 *
 * Цели — JavaScript-события; в Метрике заводятся с тем же идентификатором
 * (Настройки → Цели → JavaScript-событие). «Онбординг пройден» и «Оплата»
 * здесь не шлются: их шлёт бэкенд офлайн-конверсией (`metrika.ts`) по
 * ClientID, который отдаёт `reportClientId`, — и для бота, и для веба одной
 * целью, и даже если человек оплатил и закрыл вкладку.
 */
export const METRIKA_ID = 113667123;

export type MetrikaGoal =
  /** Проверка уровня в онбординге дошла до итога. */
  | 'app_assessment_done'
  /** Первая удачная реплика в разговоре — один раз на браузер. */
  | 'app_first_reply'
  /** Показан экран «войдите»; параметр `reason` — `turns`, `pay`, `save`. */
  | 'app_login_gate'
  /** Вход через провайдера удался; параметр `provider`. */
  | 'app_login'
  /** Создан платёж — человек ушёл на страницу ЮKassa; параметр `plan`. */
  | 'app_checkout';

type Ym = ((id: number, method: string, ...args: unknown[]) => void) & { a?: unknown[] };

function ym(method: string, ...args: unknown[]): void {
  const fn = (window as unknown as { ym?: Ym }).ym;
  // Блокировщик мог не дать загрузить tag.js — приложение от этого не ломается.
  if (typeof fn === 'function') fn(METRIKA_ID, method, ...args);
}

let started = false;

/**
 * Счётчик — до первого запроса веб-версии (`bootstrapWeb`): стандартная
 * заглушка `ym` копит вызовы, пока грузится tag.js, и цель «вошёл» из
 * колбэка Яндекса не теряется.
 */
export function initMetrika(): void {
  if (started || isTelegram()) return;
  started = true;
  const stub = ((...args: unknown[]) => {
    (stub.a ??= []).push(args);
  }) as Ym & { l?: number };
  stub.l = Date.now();
  (window as unknown as { ym?: Ym }).ym = stub;
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://mc.yandex.ru/metrika/tag.js?id=${METRIKA_ID}`;
  document.head.appendChild(script);
  ym('init', {
    webvisor: true,
    clickmap: true,
    accurateTrackBounce: true,
    trackLinks: true,
  });
}

/**
 * Просмотр страницы при переходе внутри приложения. Первый просмотр счётчик
 * шлёт сам при `init` — в том числе экрана входа, который живёт вне роутера.
 */
export function trackPageView(url: string, referer?: string): void {
  if (!started) return;
  ym('hit', url, { referer });
}

export function reachGoal(goal: MetrikaGoal, params?: Record<string, string>): void {
  if (!started) return;
  ym('reachGoal', goal, params);
}

/**
 * ClientID — на бэкенд, когда сессия уже есть: по нему уходят офлайн-цели
 * «онбординг пройден» и «оплата». Сервер пишет его только в пустое поле.
 */
export function reportClientId(): void {
  if (!started) return;
  ym('getClientID', (clientId: unknown) => {
    if (typeof clientId !== 'string' || !/^\d{1,30}$/.test(clientId)) return;
    apiRequest('/me/settings', { method: 'PATCH', body: { ymClientId: clientId } }).catch(() => undefined);
  });
}

const FIRST_REPLY_KEY = 'metrika:first-reply';

/** `app_first_reply` — один раз на браузер, а не на каждый разговор. */
export function reachFirstReply(): void {
  if (!started) return;
  try {
    if (localStorage.getItem(FIRST_REPLY_KEY)) return;
    localStorage.setItem(FIRST_REPLY_KEY, '1');
  } catch {
    return;
  }
  reachGoal('app_first_reply');
}
